import type { Account, StatusKind, View } from "../types";
import { fmtPx, fmtSigned, nowET } from "../lib/format";
import { Logo } from "./Logo";
import { AIDots } from "./AIDots";
import { StatusBadge } from "./StatusBadge";
import { useEffect, useState } from "react";

type Props = {
  accounts: Account[];
  status: { kind: StatusKind; label: string };
  ai: number;
  onNav: (view: View) => void;
  // Phase 5 D-A: optional logout handler. When provided, the
  // TopBar shows a "Sign out" button. Omitted in fixture-mode
  // tests where there's no session to revoke.
  onLogout?: () => void;
};

export const TopBar = ({
  accounts,
  status,
  ai,
  onNav,
  onLogout,
}: Props) => {
  const [time, setTime] = useState(nowET);
  useEffect(() => {
    const id = window.setInterval(() => setTime(nowET()), 1000);
    return () => window.clearInterval(id);
  }, []);

  return (
    <div className="tp-topbar">
      <div className="brand">
        <Logo size={18} />
        <span>TradePro</span>
      </div>
      <div style={{ width: 1, height: 24, background: "var(--tp-ink-5)" }} />
      <div className="tp-topbar-accts">
        {accounts.map((a) => (
          <div
            key={a.brokerAccountId}
            className={"tp-topbar-acct " + a.kind}
            title={
              a.balanceSource === "asset_class_sleeve"
                ? `Asset-class sleeve. Broker account equity ${fmtPx(a.brokerEquity ?? a.equity)}.`
                : a.balanceSource === "asset_class_exposure"
                  ? `Asset-class exposure. Set starting equity to show full sleeve cash balance. Broker account equity ${fmtPx(a.brokerEquity ?? a.equity)}.`
                  : a.balanceSource === "shared_broker_residual"
                    ? `Shared broker account balance. Non-equity sleeves are shown separately as asset-class exposure.`
                  : undefined
            }
          >
            <span className="lbl">{a.label}</span>
            <span className="value num">{fmtPx(a.equity)}</span>
            <span
              className={"delta num " + (a.todayPL >= 0 ? "pos" : "neg")}
            >
              {fmtSigned(Math.round(a.todayPL))}
            </span>
          </div>
        ))}
      </div>
      <div style={{ marginLeft: 14 }}>
        <StatusBadge kind={status.kind}>{status.label}</StatusBadge>
      </div>
      <div
        style={{
          marginLeft: 10,
          display: "inline-flex",
          gap: 8,
          alignItems: "center",
          fontSize: 12,
          color: "var(--tp-fg-3)",
        }}
      >
        <span>AI</span>
        <AIDots live={ai} />
      </div>
      <div className="grow" />
      <span
        className="num"
        style={{ color: "var(--tp-fg-3)", fontSize: 12 }}
      >
        {time}
      </span>
      <button
        type="button"
        className="iconbtn"
        title="System health"
        onClick={() => onNav("system")}
      >
        <svg
          viewBox="0 0 24 24"
          width="16"
          height="16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <path d="M3 12h4l3-7 4 14 3-7h4" />
        </svg>
      </button>
      <button
        type="button"
        className="iconbtn"
        title="Settings"
        onClick={() => onNav("config")}
      >
        <svg
          viewBox="0 0 24 24"
          width="16"
          height="16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.9 2.9l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1A1.7 1.7 0 0 0 9 19.4a1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.9-2.9l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1A1.7 1.7 0 0 0 4.6 9a1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.9-2.9l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.9 2.9l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
        </svg>
      </button>
      {onLogout && (
        <button
          type="button"
          className="iconbtn"
          title="Sign out"
          onClick={onLogout}
        >
          <svg
            viewBox="0 0 24 24"
            width="16"
            height="16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <polyline points="16 17 21 12 16 7" />
            <line x1="21" y1="12" x2="9" y2="12" />
          </svg>
        </button>
      )}
    </div>
  );
};
